import { CalendarIcon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { parseCalendarDay, useFormatDay } from "@/lib/dates"

/**
 * A document date, picked from the app's own calendar (§11.4).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REPLACES `<input type="date">` EVERYWHERE. The native control renders in the
 * BROWSER's locale and calendar, not the app's — so a Marathi screen opened a
 * date picker labelled in English, and Chrome, Safari and Android each drew a
 * different thing. The month names here come from `en.json` / `mr.json` through
 * the same `t()` every other string uses.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TIMEZONE TRAP, AND WHY THIS FILE IS THE ONLY PLACE ALLOWED TO BUILD A
 * `Date` FROM A DOCUMENT DATE.
 *
 * `lib/dates.ts` deliberately contains no `Date` at all: a document date is a
 * CALENDAR DAY, the API sends `"2026-08-10"`, and `new Date("2026-08-10")`
 * parses that as UTC MIDNIGHT — which is the 9th in every negative-offset zone.
 * The owner is in IST and would never see it; the son on a laptop still set to
 * another zone would, and an invoice whose screen date is one day off its PDF
 * is the sort of bug a customer finds first.
 *
 * But react-day-picker needs real `Date` objects. So the conversion happens
 * HERE, once, and in the only safe way:
 *
 *  - STRING → DATE builds `new Date(y, m - 1, d)` from the split parts, which
 *    is LOCAL midnight of exactly that calendar day. Never `new Date(iso)`.
 *  - DATE → STRING reads `getFullYear/getMonth/getDate`, which are LOCAL.
 *    Never `toISOString()` — that converts to UTC and re-introduces the very
 *    off-by-one-day this exists to avoid.
 *
 * Both directions therefore stay inside one calendar day, whatever the machine
 * is set to.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** `"2026-08-10"` → local midnight on that day, or `undefined`. */
export function isoToLocalDate(iso: string | null | undefined): Date | undefined {
  const parts = parseCalendarDay(iso)
  if (parts === null) return undefined
  return new Date(parts.year, parts.month - 1, parts.day)
}

/** A `Date` → `"2026-08-10"`, read in LOCAL time. Never `toISOString()`. */
export function localDateToIso(date: Date | undefined): string {
  if (date === undefined) return ""
  const year = String(date.getFullYear()).padStart(4, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export function DateField({
  value,
  onChange,
  id,
  ariaLabel,
  placeholder,
  className,
  disabled = false,
  clearable = false,
}: {
  /** `"YYYY-MM-DD"`, or `""` for no date. The same shape the API takes. */
  value: string
  onChange: (value: string) => void
  id?: string
  ariaLabel?: string
  placeholder?: string
  className?: string
  disabled?: boolean
  /** Offer "clear" — for filters, where "no date" is a real answer. */
  clearable?: boolean
}): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const [open, setOpen] = useState(false)

  const selected = isoToLocalDate(value)
  // Formatted through the SAME helper the rest of the app uses, so a date reads
  // identically on this button, in the register and on the detail screen.
  const label = value === "" ? "" : formatDay(value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            aria-label={ariaLabel}
            className={cn(
              "w-full justify-start text-left font-normal tabular-nums",
              label === "" && "text-muted-foreground",
              className,
            )}
          />
        }
      >
        <CalendarIcon className="size-4 shrink-0" />
        {label === "" ? (placeholder ?? t("lbl.date")) : label}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          autoFocus
          /*
           * MONTH AND WEEKDAY NAMES COME FROM `en.json` / `mr.json`, not from
           * `toLocaleString` (which is what the shadcn Calendar does by
           * default). §11.4 requires every user-facing string to go through
           * `t()`, and the browser's Marathi month data is not something this
           * app should be trusting on a legal document's date picker — the
           * whole reason `lib/dates.ts` keeps its own twelve month keys.
           *
           * The parts are read with LOCAL getters for the same reason the
           * conversion helpers above use them.
           */
          formatters={{
            formatCaption: (month) =>
              `${t(`date.mon.${String(month.getMonth() + 1)}`)} ${String(month.getFullYear())}`,
            formatMonthDropdown: (month) => t(`date.mon.${String(month.getMonth() + 1)}`),
            formatWeekdayName: (day) => t(`date.dow.${String(day.getDay())}`),
          }}
          onSelect={(next) => {
            onChange(localDateToIso(next))
            // Closing on pick is right for a single date: there is nothing more
            // to choose, and leaving it open hides the field he just filled.
            setOpen(false)
          }}
        />
        {clearable && value !== "" ? (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                onChange("")
                setOpen(false)
              }}
            >
              {t("btn.clear")}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
